import React from 'react';
import { ReactComponent as PlaySolidIcon } from 'resources/images/play-solid.svg';
import { ReactComponent as PauseSolidIcon } from 'resources/images/pause-solid.svg';
import { TEST_ID_LOADING_INDICATOR } from 'app/utils/test/constants';
import { Center, Icon, Spinner, SpinnerProps } from '@chakra-ui/react';
import { DataTestId } from 'types/general.types';
import { SystemStyleObject } from '@chakra-ui/styled-system';

interface AudioActionButtonProps extends DataTestId {
  playing?: boolean;
  isLoading?: boolean;
  isActive?: boolean;
  size?: number;
  isPlayerTheme?: boolean;
  playIconTestId?: string;
  pauseIconTestId?: string;
  thickness?: number;
  iconToButtonRatio?: number;
  spinnerProps?: SpinnerProps;
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  isDisabled?: boolean;
  variant?: 'audio' | 'player' | 'cover';
  sx?: SystemStyleObject;
}

export const AudioActionButton: React.FC<AudioActionButtonProps> = ({
  playing,
  isLoading,
  size = 36,
  pauseIconTestId,
  playIconTestId,
  thickness = 2,
  iconToButtonRatio = 0.5,
  spinnerProps,
  onClick,
  isDisabled,
  variant = 'player',
  sx,
  'data-testid': dataTestId,
}) => {
  if (isLoading) {
    return (
      <Spinner
        gridArea="play"
        thickness={`${thickness}px`}
        data-testid={`audio-${TEST_ID_LOADING_INDICATOR}`}
        width={`${size}px`}
        minWidth={`${size}px`}
        height={`${size}px`}
        minHeight={`${size}px`}
        {...spinnerProps}
      />
    );
  }

  const iconSize = size ? size * iconToButtonRatio : 8;

  const buttonSx =
    variant === 'audio'
      ? {
          bg: 'M.50',
          color: 'M.10',
          active: {
            bg: 'M.70',
          },
        }
      : variant === 'cover'
      ? {
          bg: 'transparent',
          color: 'M.70',
        }
      : {
          bg: 'M.70',
          color: 'M.10',
        };

  return (
    <Center
      gridArea="play"
      as="button"
      borderRadius="full"
      flexShrink={0}
      width={`${size}px`}
      height={`${size}px`}
      onClick={onClick}
      data-testid={dataTestId}
      _hover={{
        transform: 'scale(1.02)',
        ...buttonSx.active,
      }}
      _groupHover={{
        transform: 'scale(1.02)',
        ...buttonSx.active,
      }}
      _disabled={{
        opacity: 0.36,
        cursor: 'default',
      }}
      disabled={isDisabled}
      bg={playing ? buttonSx.active?.bg || buttonSx.bg : buttonSx.bg}
      sx={sx}
    >
      <Icon
        as={playing ? PauseSolidIcon : PlaySolidIcon}
        data-testid={playing ? pauseIconTestId : playIconTestId}
        width={`${iconSize}px`}
        height={`${iconSize}px`}
        color={buttonSx.color}
      />
    </Center>
  );
};
